import { useState } from "react";
import { Mail, MapPin, Phone, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "../../assets/Revibelogo.png";
import {
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
} from "react-icons/fa";

const Footer = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <footer className="bg-[#d0d9e5] backdrop-blur-md text-gray-800">
      {/* Top Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 xl:px-16 pt-12 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-14">
        {/* Brand + Description */}
        <div>
          <div className="flex items-center mb-4">
            <img
              src={logo}
              alt="Revibe Logo"
              className="h-12 w-auto drop-shadow-md"
            />
          </div>
          <p className="text-sm leading-relaxed text-gray-700">
            We’re here to help you on your fitness journey! From subscriptions
            to technical assistance, our team is always ready to assist you.
          </p>

          <div className="mt-6 space-y-3 text-sm">
            <p className="flex items-center gap-2">
              <Phone size={16} className="text-lime-500" /> +91 99677 22756
            </p>
            <p className="flex items-center gap-2">
              <Mail size={16} className="text-lime-500" /> api@getrevibe.app
            </p>
            <p className="flex items-center gap-2">
              <MapPin size={24} className="text-lime-500" /> 2206, 22nd Floor,
              Solus, Hiranandani Estate, Patlipada, Thane (West),
              <br /> Maharashtra – 400607
            </p>
          </div>
        </div>

        {/* Site Links */}
        <div>
          <button
            className="w-full flex items-center justify-between lg:justify-start lg:mb-4 font-semibold text-lg text-gray-900"
            onClick={() => toggleSection("site")}
          >
            Site Links
            <ChevronDown
              className={`lg:hidden transform transition-transform ${
                openSection === "site" ? "rotate-180" : ""
              }`}
            />
          </button>
          <ul
            className={`space-y-2 text-sm mt-2 lg:mt-0 ${
              openSection === "site" ||
              (typeof window !== "undefined" && window.innerWidth >= 1024)
                ? "block"
                : "hidden lg:block"
            }`}
          >
            {["Home", "About", "Pricing", "Contact"].map((item) => (
              <li key={item}>
                <Link
                  to={`/${
                    item === "Home" ? "" : item.toLowerCase().replace(" ", "")
                  }`}
                  className="hover:text-lime-600 transition-colors"
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Policies */}
        <div>
          <button
            className="w-full flex items-center justify-between lg:justify-start lg:mb-4 font-semibold text-lg text-gray-900"
            onClick={() => toggleSection("policies")}
          >
            Policies
            <ChevronDown
              className={`lg:hidden transform transition-transform ${
                openSection === "policies" ? "rotate-180" : ""
              }`}
            />
          </button>
          <ul
            className={`space-y-2 text-sm mt-2 lg:mt-0 ${
              openSection === "policies" ||
              (typeof window !== "undefined" && window.innerWidth >= 1024)
                ? "block"
                : "hidden lg:block"
            }`}
          >
            {[
              "Privacy Policy",
              "Terms Conditions",
              "Refund Policy",
              "Shipping Policy",
            ].map((item) => (
              <li key={item}>
                <Link
                  to={`/${item.toLowerCase().replace(/\s+/g, "-")}`}
                  className="hover:text-lime-600 transition-colors"
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Connect + Newsletter */}
        <div>
          <button
            className="w-full flex items-center justify-between lg:justify-start lg:mb-4 font-semibold text-lg text-gray-900"
            onClick={() => toggleSection("connect")}
          >
            Connect With Us
            <ChevronDown
              className={`lg:hidden transform transition-transform ${
                openSection === "connect" ? "rotate-180" : ""
              }`}
            />
          </button>

          <div
            className={`mt-2 lg:mt-0 space-y-4 ${
              openSection === "connect" ||
              (typeof window !== "undefined" && window.innerWidth >= 1024)
                ? "block"
                : "hidden lg:block"
            }`}
          >
            <p className="text-sm text-gray-600">
              Stay updated with fitness tips, new classes, and community
              updates.
            </p>

            <div className="mt-6">
              <div className="flex space-x-4">
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/revibeapp_"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
                >
                  <FaInstagram className="text-pink-500 text-xl" />
                </a>

                {/* Facebook */}
                <a
                  href="https://www.facebook.com/people/Revibe-Health-And-Happiness"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
                >
                  <FaFacebook className="text-blue-600 text-xl" />
                </a>

                {/* Twitter */}
                <a
                  href="https://x.com/revibe2025"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
                >
                  <FaTwitter className="text-sky-500 text-xl" />
                </a>

                {/* YouTube */}
                <a
                  href="https://www.youtube.com/@Revibe2025"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
                >
                  <FaYoutube className="text-red-600 text-xl" />
                </a>
                <a
                  href="https://www.linkedin.com/company/revibe-health-and-happiness"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:scale-110 transition transform"
                >
                  <FaLinkedin className="text-blue-600 text-xl" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      {/* <div className="border-t border-gray-300 text-center text-sm text-gray-600 py-4">
        © {new Date().getFullYear()} Revibe Health and Happiness. All Rights
        Reserved.
      </div> */}
    </footer>
  );
};

export default Footer;
